import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { AiGeometry } from "@constructionautomation/backend/convex/takeoffPilot/aiMeasurementGeometry";
import { useLoggedMutation } from "@/hooks/use-action-log-session";
import type { UndoRedoAction } from "@/hooks/use-undo-redo-history";
import type { DraftMeasurementState } from "@/viewport-tools/measurement/overlay";
import { AiHistoryLineage } from "./ai-history-lineage";
import { getAiHistorySession } from "./ai-history-session";
import { MeasurementHistoryLineage } from "./measurement-history-lineage";

export type AiDrawing =
  | {
      kind: "new";
      runId: Id<"takeoffPilotAiRuns">;
      tagId: Id<"takeoffPilotExtractedTags">;
      basis: "LF" | "SQFT";
      requestKey: string;
    }
  | {
      kind: "trace";
      proposalId: Id<"takeoffPilotAiProposals">;
      version: number;
      previousGeometryJson: string;
      basis: "LF" | "SQFT";
      mode: "replace" | "cutout";
      tagId: Id<"takeoffPilotExtractedTags">;
    };

type Trace = Pick<
  Extract<AiDrawing, { kind: "trace" }>,
  "proposalId" | "version" | "previousGeometryJson"
>;

/** Persists AI drawings and records trace history; the viewer owns draft and tool state. */
export function useAiDrawing(record: (action: UndoRedoAction) => void, lineage: AiHistoryLineage, measurementHistory: MeasurementHistoryLineage) {
  const traceAiProposal = useLoggedMutation(
    api.takeoffPilot.aiMeasurements.traceProposal,
  );
  const restoreTraceState = useLoggedMutation(
    api.takeoffPilot.aiMeasurements.restoreTraceState,
  );
  const addAiMeasurement = useLoggedMutation(
    api.takeoffPilot.aiMeasurements.addMeasuredItem,
  );

  const saveTrace = async (trace: Trace, next: AiGeometry) => {
    const lineageSessionId = await getAiHistorySession();
    const convert = (
      geometry: AiGeometry,
      expectedVersion: number,
      expectedGeometryJson?: string,
    ) =>
      traceAiProposal({
        proposalId: trace.proposalId,
        lineageSessionId,
        expectedVersion,
        ...(expectedGeometryJson === undefined ? {} : { expectedGeometryJson }),
        basis: geometry.kind === "point" ? "count" : geometry.kind === "polygon" ? "SQFT" : "LF",
        vertices: geometry.points.map(([x, y]) => ({ x, y })),
        ...(geometry.kind === "polygon"
          ? { holes: (geometry.holes ?? []).map((ring) => ring.map(([x, y]) => ({ x, y }))) }
          : {}),
      });
    const cursors = lineage.track([{ proposalId: trace.proposalId, version: trace.version }]);
    const apply = async (geometry: AiGeometry, expectedGeometryJson: string) => {
      const [{ version }] = lineage.expected(cursors);
      const result = await convert(geometry, version, expectedGeometryJson);
      lineage.advance(cursors, [{ proposalId: trace.proposalId, beforeVersion: version, version: result.version }]);
      return result;
    };
    const result = await apply(next, trace.previousGeometryJson);
    const measurementCursors = measurementHistory.track(result.measurementVersions);
    measurementHistory.advance(measurementCursors, result.measurementVersions);
    const restore = async (direction: "undo" | "redo") => {
      const [{ version }] = lineage.expected(cursors);
      const restored = await restoreTraceState({ actionId: result.actionId, direction, expectedVersion: version, lineageSessionId, expectedMeasurementVersions: measurementHistory.expected(measurementCursors) });
      lineage.advance(cursors, [{ proposalId: trace.proposalId, beforeVersion: version, version: restored.version }]);
      measurementHistory.advance(measurementCursors, restored.measurementVersions);
    };
    record({
      undo: async () => {
        await restore("undo");
      },
      redo: async () => {
        await restore("redo");
      },
    });
    return result.measurementId;
  };

  const saveDrawing = async (drawing: AiDrawing, draft: DraftMeasurementState) => {
    if (
      draft.tagId !== drawing.tagId ||
      draft.measurementType !== drawing.basis ||
      draft.tool !== (drawing.basis === "SQFT" ? "polygon" : "polyline") ||
      draft.geometry.type !== "poly"
    ) {
      throw new Error("Use the selected AI item's tag and measurement tool before saving");
    }
    let geometry: AiGeometry = {
      kind: drawing.basis === "SQFT" ? "polygon" : "polyline",
      points: draft.geometry.vertices.map((p) => [p.x, p.y]),
      ...(drawing.basis === "SQFT"
        ? { holes: (draft.geometry.holes ?? []).map((ring) => ring.map((p) => [p.x, p.y])) }
        : {}),
    };
    if (drawing.kind === "trace" && drawing.mode === "cutout") {
      const previous = JSON.parse(drawing.previousGeometryJson) as AiGeometry;
      geometry = { ...previous, holes: [...(previous.holes ?? []), geometry.points] };
    } else if (
      drawing.kind === "trace" &&
      drawing.basis === "SQFT" &&
      draft.geometry.holes === undefined
    ) {
      geometry.holes = (JSON.parse(drawing.previousGeometryJson) as AiGeometry).holes ?? [];
    }
    if (drawing.kind === "trace") return { measurementId: await saveTrace(drawing, geometry), created: false, aiVersions: [] };
    return addAiMeasurement({
      runId: drawing.runId,
      tagId: drawing.tagId,
      requestKey: drawing.requestKey,
      geometryJson: JSON.stringify(geometry),
    });
  };

  return { saveDrawing, saveTrace };
}
